'use client'

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Compass,
  Plane,
  Luggage,
  Car,
  Stamp,
  User,
  Settings,
  LifeBuoy,
  LogOut,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import axios from "axios";
import { API_URL } from "../config";

interface MenuItem {
  id: string;
  icon: LucideIcon;
  label: string;
  href: string;
  active: boolean;
  badge?: number;
}

interface UserProfile {
  id: number;
  name: string;
  email: string;
  client?: {
    firstname: string;
    lastname: string;
    phone?: string;
    address?: string;
    country?: string;
  };
}

const SidebarCustomer = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const menuItems: MenuItem[] = [
    {
      id: "dashboard",
      icon: Compass,
      label: "Dashboard",
      href: "/my-dashboard",
      active: pathname === "/my-dashboard",
    },
    {
      id: "flights",
      icon: Plane,
      label: "Manage Flights",
      href: "/my-flight",
      active: pathname === "/my-flight",
    },
    {
      id: "trips",
      icon: Luggage,
      label: "Manage Trips",
      href: "/my-trips",
      active: pathname === "/my-trips",
    },
    {
      id: "cars",
      icon: Car,
      label: "Manage Cars",
      href: "/my-rent-cars",
      active: pathname === "/my-rent-cars",
    },
    {
      id: "visa",
      icon: Stamp,
      label: "Visa Requests",
      href: "/my-request-visa",
      active: pathname === "/my-request-visa",
      badge: 3,
    },
  ];

  const secondaryMenu: MenuItem[] = [
    {
      id: "profile",
      icon: User,
      label: "My Profile",
      href: "/my-profile",
      active: pathname === "/my-profile",
    },
    {
      id: "support",
      icon: LifeBuoy,
      label: "Support",
      href: "/support",
      active: pathname === "/support",
    },
  ];

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem("token_customer_silverjarz");
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_URL}/customer/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          setUser(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token_customer_silverjarz");
    // Redirect to login page
    window.location.href = "/login";
  };

  const renderMenu = (items: MenuItem[]) => (
    <ul className="nav-menu">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li key={item.id} className="nav-item">
            <a href={item.href} className={`nav-link ${item.active ? "active" : ""}`}>
              <span className="nav-icon-wrap">
                <Icon size={17} strokeWidth={2} />
              </span>
              <span className="nav-label">{item.label}</span>
              {!!item.badge && <span className="nav-badge">{item.badge}</span>}
              {item.active && <ChevronRight size={14} className="nav-chevron" />}
            </a>
          </li>
        );
      })}
    </ul>
  );

  // Get user display name
  const getDisplayName = () => {
    if (!user) return "Guest";
    if (user.client?.firstname && user.client?.lastname) {
      return `${user.client.firstname} ${user.client.lastname}`;
    }
    return user.name || "User";
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (!user) return "G";
    if (user.client?.firstname && user.client?.lastname) {
      return `${user.client.firstname[0]}${user.client.lastname[0]}`;
    }
    if (user.name) {
      const nameParts = user.name.split(" ");
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`;
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  // Get user email
  const getEmail = () => {
    if (!user) return "guest@example.com";
    return user.email || "No email";
  };

  return (
    <>
      <style>{`
        .sidebar-customer {
          --p: #294b5a;
          --p-light: #3a6275;
          --p-dark: #1a313d;
          --gold: #d3be76;
          --gold-dk: #b8a258;
          --muted: #7a8a91;
          font-family: 'DM Sans', sans-serif;
          height: 100%;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border-right: 1px solid rgba(41, 75, 90, 0.08);
          padding: 22px 0 18px;
          max-width: 260px;
        }

        /* Brand row */
        .sidebar-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0 20px 20px;
          margin-bottom: 8px;
          border-bottom: 1px solid rgba(41, 75, 90, 0.06);
        }
        .sidebar-brand-mark {
          width: 32px; height: 32px; border-radius: 9px;
          background: linear-gradient(135deg, var(--p), var(--p-light));
          display: flex; align-items: center; justify-content: center;
          color: var(--gold); flex-shrink: 0;
        }
        .sidebar-brand-text { display: flex; flex-direction: column; line-height: 1.2; }
        .sidebar-brand-text strong { font-size: 13.5px; font-weight: 700; color: var(--p-dark); }
        .sidebar-brand-text span { font-size: 11px; color: var(--muted); }

        /* Menu Navigation */
        .sidebar-nav {
          flex: 1;
          padding: 0 14px;
          overflow-y: auto;
        }

        .nav-section {
          margin-bottom: 22px;
        }

        .nav-section-title {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: var(--muted);
          padding: 0 12px;
          margin-bottom: 10px;
        }

        .nav-menu {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .nav-item {
          margin-bottom: 2px;
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 10px;
          color: var(--p-light);
          text-decoration: none;
          font-size: 13.5px;
          font-weight: 500;
          border-left: 3px solid transparent;
          transition: background .15s ease, color .15s ease, border-color .15s ease;
        }

        .nav-link:hover {
          background: rgba(211, 190, 118, 0.10);
          color: var(--p);
        }

        .nav-link.active {
          background: rgba(211, 190, 118, 0.14);
          color: var(--gold-dk);
          font-weight: 700;
          border-left-color: var(--gold);
        }

        .nav-icon-wrap {
          width: 30px; height: 30px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(41, 75, 90, 0.06); color: var(--p-light);
          flex-shrink: 0; transition: background .15s ease, color .15s ease;
        }
        .nav-link:hover .nav-icon-wrap { background: rgba(211, 190, 118, 0.18); color: var(--p); }
        .nav-link.active .nav-icon-wrap { background: rgba(211, 190, 118, 0.22); color: var(--gold-dk); }

        .nav-label { flex: 1; }

        .nav-badge {
          padding: 2px 7px;
          background: var(--gold);
          color: var(--p-dark);
          font-size: 10px;
          font-weight: 700;
          border-radius: 12px;
        }

        .nav-chevron { color: var(--gold-dk); flex-shrink: 0; }

        /* Footer / profile card */
        .sidebar-footer {
          padding: 16px 14px 0;
          margin-top: auto;
          border-top: 1px solid rgba(41, 75, 90, 0.06);
        }
        .sidebar-profile {
          display: flex; align-items: center; gap: 10px;
          padding: 10px; border-radius: 12px;
          background: rgba(41, 75, 90, 0.03);
          border: 1px solid rgba(41, 75, 90, 0.06);
        }
        .sidebar-avatar {
          width: 34px; height: 34px; border-radius: 50%;
          background: var(--p); color: #fff; font-size: 12.5px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sidebar-profile-text { flex: 1; min-width: 0; line-height: 1.25; }
        .sidebar-profile-text strong {
          display: block; font-size: 12.5px; font-weight: 700; color: var(--p-dark);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .sidebar-profile-text span {
          display: block; font-size: 11px; color: var(--muted);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .sidebar-logout {
          width: 28px; height: 28px; border-radius: 8px; border: none;
          background: transparent; color: var(--muted); cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background .15s ease, color .15s ease; flex-shrink: 0;
        }
        .sidebar-logout:hover { background: rgba(211, 74, 74, 0.10); color: #c0392b; }

        /* Scrollbar */
        .sidebar-nav::-webkit-scrollbar { width: 4px; }
        .sidebar-nav::-webkit-scrollbar-track { background: rgba(41, 75, 90, 0.05); }
        .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(211, 190, 118, 0.3); border-radius: 4px; }
        .sidebar-nav::-webkit-scrollbar-thumb:hover { background: var(--gold); }

        /* Skeleton loading */
        @keyframes skeleton-pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        .skeleton-text {
          background: #e0e3e6;
          border-radius: 4px;
          animation: skeleton-pulse 1.5s ease-in-out infinite;
        }
        .skeleton-avatar {
          background: #e0e3e6;
          border-radius: 50%;
          animation: skeleton-pulse 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="sidebar-customer">
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand-mark">
            <Stamp size={16} />
          </div>
          <div className="sidebar-brand-text">
            <strong>Customer Space</strong>
            <span>Manage your bookings</span>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">Main Menu</div>
            {renderMenu(menuItems)}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Account</div>
            {renderMenu(secondaryMenu)}
          </div>
        </div>

        {/* Profile / footer */}
        <div className="sidebar-footer">
          <div className="sidebar-profile">
            {loading ? (
              <>
                <div className="skeleton-avatar" style={{ width: 34, height: 34 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="skeleton-text" style={{ width: "60%", height: 14, marginBottom: 4 }} />
                  <div className="skeleton-text" style={{ width: "80%", height: 12 }} />
                </div>
                <div className="skeleton-text" style={{ width: 28, height: 28, borderRadius: 8 }} />
              </>
            ) : (
              <>
                <div className="sidebar-avatar">{getInitials()}</div>
                <div className="sidebar-profile-text">
                  <strong>{getDisplayName()}</strong>
                  <span>{getEmail()}</span>
                </div>
                <button 
                  className="sidebar-logout" 
                  title="Log out" 
                  aria-label="Log out"
                  onClick={handleLogout}
                >
                  <LogOut size={15} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SidebarCustomer;